import { useEffect, useMemo, useState } from "react";
import { Form, Row, Col, Input, Button, Select, Upload, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import requets from "../../api";
import { uploadEntityImage } from "../../api/service/cdn/cdn";
import { useTranslation } from "react-i18next";
import { useAppAuth } from "../../context/use-app-auth";

const normalizeUploadFile = (e) => {
  if (Array.isArray(e)) return e;
  return e?.fileList;
};

export default function ProductNew({ onSuccess }) {
  const { t } = useTranslation("global");
  const auth = useAppAuth();
  const req = useMemo(() => requets(auth.user?.access_token), [auth.user?.access_token]);
  const [form] = Form.useForm();
  const [messageSend, contextHolder] = message.useMessage();
  const [measurements, setMeasurements] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    req
      .get("/measurement", { limit: 500 })
      .then((response) => {
        const dataArray = Array.isArray(response.data)
          ? response.data
          : (response.data?.data || []);
        setMeasurements(dataArray.map((m) => ({ label: m.name, value: m.uuid })));
      })
      .catch(() => messageSend.error(t("products.errorMeasurements")));
  }, []);

  const onFinish = async (value) => {
    setUploading(true);
    try {
      const iconFile = value.icon?.[0];

      // 1. Create the product (without icon — backend assigns UUID first)
      const created = await req.post("/product", {
        name: value.name,
        description: value.description,
        type: value.type,
        version: value.version,
        measurements: value.measurements,
      });

      // 2. Upload icon to the entity endpoint so the backend proxies it to the CDN
      const newUuid = created.data?.uuid;
      if (iconFile?.originFileObj && newUuid) {
        await uploadEntityImage(req, `/product/${newUuid}/image`, iconFile.originFileObj);
      }

      messageSend.success(t("products.successCreated"));
      form.resetFields();
      onSuccess?.();
    } catch {
      messageSend.error(t("products.errorLoad"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Row wrap gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col xs={24} sm={12} md={8} lg={7}>
            <Form.Item
              label={t("products.tableColumn.name")}
              name="name"
              rules={[{ required: true, message: t("products.requiredName") }]}
            >
              <Input placeholder={t("products.tableColumn.placeholderName")} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={7}>
            <Form.Item
              label={t("products.tableColumn.productType")}
              name="type"
              rules={[{ required: true, message: t("products.requiredType") }]}
            >
              <Input placeholder={t("products.tableColumn.placeholderType")} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={7}>
            <Form.Item
              label={t("products.tableColumn.version")}
              name="version"
              rules={[{ required: true, message: t("products.requiredVersion") }]}
            >
              <Input placeholder={t("products.tableColumn.placeholderVersion")} />
            </Form.Item>
          </Col>
        </Row>
        <Row wrap gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item name="description" label={t("products.tableColumn.description")}>
              <Input placeholder={t("products.tableColumn.placeholderDescription")} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label={t("products.tableColumn.newMeasurements")}
              name="measurements"
              rules={[{ required: true, message: t("products.requiredMeasurements") }]}
            >
              <Select
                mode="tags"
                size="large"
                allowClear
                placeholder={t("products.tableColumn.placeholderMeasurements")}
                style={{ width: "100%" }}
                options={measurements}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row wrap gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col xs={24} sm={8} md={6} lg={4}>
            <Form.Item
              label={t("products.tableColumn.icon")}
              name="icon"
              valuePropName="fileList"
              getValueFromEvent={normalizeUploadFile}
            >
              <Upload
                listType="picture-card"
                maxCount={1}
                beforeUpload={() => false}
                accept=".jpg,.jpeg,.png,.webp"
              >
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>{t("products.uploadButton")}</div>
                </div>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
          <Button shape="round" htmlType="submit" type="primary" loading={uploading}>
            {t("deviceEdit.saveChange")}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
}
